import React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Badge,
} from 'lift-ui';
import { PermissionToRemove } from '../../types';
import { formatProductName } from '../../utils';
import '../../iam-support.styles.scss';

interface RemovePermissionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permissionToRemove: PermissionToRemove | null;
  onRemove: (userId: number, product: string) => void;
  isSubmitting?: boolean;
}

const RemovePermissionSheet: React.FC<RemovePermissionSheetProps> = ({
  open,
  onOpenChange,
  permissionToRemove,
  onRemove,
  isSubmitting = false,
}) => {
  const handleRemove = () => {
    if (permissionToRemove) {
      onRemove(permissionToRemove.user.id, permissionToRemove.product);
      onOpenChange(false);
    }
  };

  const productName = permissionToRemove ? formatProductName(permissionToRemove.product) : '';
  const currentLevel = permissionToRemove?.user.permissions[permissionToRemove.product];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" className="iam-support-sheet flex flex-col h-full">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Remove Permission
          </SheetTitle>
          <SheetDescription>
            Remove access to a product for a user. This action can be undone by reassigning the permission.
          </SheetDescription>
        </SheetHeader>
        <div className="iam-support-dialog-form flex-1 overflow-y-auto">
          {/* User Info */}
          <div className="p-4 bg-gray-50 rounded-lg border mb-4">
            <div className="text-sm text-gray-500 mb-1">User</div>
            <div className="font-medium">{permissionToRemove?.user.name}</div>
            <div className="text-gray-600 text-sm">{permissionToRemove?.user.email}</div>
          </div>

          {/* Permission to Remove */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-sm text-red-600 mb-2">Permission to remove:</div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">{productName}</span>
              {currentLevel && (
                <Badge variant="outline" className="bg-white text-red-700 border-red-300">
                  {currentLevel}
                </Badge>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> If this user is a member of a group that has this permission, 
              they may still have access through group-based permissions.
            </p>
          </div>
        </div>
        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleRemove}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Removing...' : 'Remove Permission'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RemovePermissionSheet;
